import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { Box } from '#/components/Box/Box';
import { CenteredContent } from '#/components/CenteredContent/CenteredContent';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { Text } from '#/components/Text/Text';
import { useAppConfig } from '#/config/AppConfigContext';
import { SignupFormContainer } from '#/containers/SignupFormContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useIsFirstRenderRef } from '#/hooks/useIsFirstRenderRef';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import { MovementSuccessApplicationMessage } from '#/pages/movement/MovementSuccessApplicationMessage';
import { isMovementPageRedirectState } from '#/pages/movement/movementPageUtils';
import { WydSuccessApplicationMessage } from '#/pages/wyd/WydSuccessApplicationMessage';
import { isWydPageRedirectState } from '#/pages/wyd/wydPageUtils';
import { homeRoute } from '#/routing/routes';
import { allowRedirect } from '#/utils/allowRedirect';
import { isPathOnlyUrl } from '#/utils/isPathOnlyUrl';
import { authUserInviteRoute } from '#/modules/auth/routing/auth.routes';

export type AuthSignupPageLocationState = {
  redirectTo?: string | null;
  email?: string;
  tmpStorage?: AuthApiTmpStorage;
};

type Props = {
  invite: ApiInvite | null;
};

export function AuthSignupPage({ invite }: Props) {
  const { i18n, locale } = useI18n();
  const { appConfig } = useAppConfig();

  const navigate = useNavigate();

  const { user } = useUser();

  const { isFirstRenderRef } = useIsFirstRenderRef();
  useEffect(() => {
    // Redirect to home if the user lands on the login page while logged in
    if (isFirstRenderRef.current && user)
      void navigate(homeRoute.with({ lang: locale }));
  }, [locale, isFirstRenderRef, navigate, user]);

  const location = useLocation<AuthSignupPageLocationState>();

  const [searchParams] = useSearchParams();
  const tmpStorage = location.state?.tmpStorage;
  const email = location.state?.email;
  const redirectTo = searchParams.get('to') ?? location.state?.redirectTo;

  const movementPageFormValues = isMovementPageRedirectState(location.state)
    ? location.state.movementPageFormValues
    : null;

  const wydPageFormValues = isWydPageRedirectState(location.state)
    ? location.state.wydPageFormValues
    : null;

  const interestFormRedirect = movementPageFormValues || wydPageFormValues;

  useTrackPageView('Sign Up', {
    page_type: 'Sign Up',
  });

  const redirect = () => {
    // If invite exists, go to accept page
    if (invite) {
      void navigate(authUserInviteRoute.with({ inviteId: invite.id }));
      return;
    }

    // Normal flow for non-invite signups
    let redirectToValue = redirectTo;

    if (!redirectToValue || !allowRedirect(redirectToValue, appConfig)) {
      redirectToValue = `/${locale}/`;
    }

    if (!isPathOnlyUrl(redirectToValue)) {
      window.location.assign(redirectToValue);
      return;
    }

    void navigate(redirectToValue);
  };

  return (
    <CenteredContent>
      {!interestFormRedirect && (
        <Box textAlign="center">
          <IdealistLogo height={82} />
        </Box>
      )}

      {movementPageFormValues && <MovementSuccessApplicationMessage />}
      {wydPageFormValues && <WydSuccessApplicationMessage />}

      {!interestFormRedirect && (
        <Text
          fontSize="24px"
          fontWeight="semiBold"
          color="mediumContentGrey"
          textAlign="center"
          mb="8px"
        >
          {
            /* TRANSLATORS: signup page title */
            i18n('Sign Up')
          }
        </Text>
      )}

      <SignupFormContainer
        analyticsTitle="[main] Signup page"
        usageContext="page"
        redirectTo={redirectTo}
        email={email}
        movementPageFormValues={movementPageFormValues}
        wydPageFormValues={wydPageFormValues}
        invite={invite}
        tmpStorage={tmpStorage}
        onSignup={redirect}
        onLogin={undefined}
      />
    </CenteredContent>
  );
}
